import { TableComponent as DataTable } from 'components/TableComponent'
import PMBPagination from 'components/PMBPagination'
import { useEffect, useMemo, useState } from 'react'
import { ChevronUp } from 'react-feather'
import { Card, CardBody } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const getValue = (side) => Number(side?.totalAfterVAT ?? side?.totalValue ?? 0)
const getDisplayUnit = (row) => `${row?.unit || ''}${row?.units?.length > 1 ? '+' : ''}`
const monthLabels = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

export const buildYearlyMonths = (data) => {
  const purchaseMonths = data?.purchase?.months || []
  const salesMonths = data?.sales?.months || []
  const differenceMonths = data?.difference?.months || []

  return monthLabels.map((label, index) => {
    const month = index + 1
    const purchase = purchaseMonths.find((item) => Number(item.month) === month) || { transactionCount: 0, totalAfterVAT: 0 }
    const sales = salesMonths.find((item) => Number(item.month) === month) || { transactionCount: 0, totalAfterVAT: 0 }
    const difference = differenceMonths.find((item) => Number(item.month) === month) || {
      totalAfterVAT: getValue(sales) - getValue(purchase),
    }
    return { label, month, purchase, sales, difference }
  })
}

export const groupByItemId = (rows) => {
  const groups = new Map()
  rows.forEach((row) => {
    const key = String(row.id)
    if (!groups.has(key)) {
      groups.set(key, { id: row.id, name: row.name, units: [], purchase: { totalAfterVAT: 0 }, sales: { totalAfterVAT: 0 } })
    }
    const group = groups.get(key)
    group.units.push(row)
    group.purchase.totalAfterVAT += getValue(row.purchase)
    group.sales.totalAfterVAT += getValue(row.sales)
  })
  return Array.from(groups.values()).map((group) => {
    const dominantSalesUnit = group.units.reduce((largest, row) =>
      getValue(row.sales) > getValue(largest.sales) ? row : largest,
    group.units[0])
    return {
      ...group,
      unit: dominantSalesUnit.unit,
      purchase: {
        ...group.purchase,
        transactionCount: group.units.reduce((sum, row) => sum + Number(row.purchase?.transactionCount || 0), 0),
        effectiveAmount: dominantSalesUnit.purchase?.effectiveAmount,
      },
      sales: {
        ...group.sales,
        transactionCount: group.units.reduce((sum, row) => sum + Number(row.sales?.transactionCount || 0), 0),
        effectiveAmount: dominantSalesUnit.sales?.effectiveAmount,
      },
      differenceAmount: dominantSalesUnit.differenceAmount,
      differenceValue: group.sales.totalAfterVAT - group.purchase.totalAfterVAT,
    }
  })
}

const SummaryTable = ({ activeTab, summaryData, monthlyRows = [], isLoading: isSummaryLoading, dateRange, counterpartId, itemId }) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortdir, setSortdir] = useState('ASC')

  // Reset page and sort when switching tabs
  useEffect(() => {
    setCurrentPage(0)
    setSortdir('ASC')
  }, [activeTab])

  useEffect(() => {
    setCurrentPage(0)
  }, [dateRange, counterpartId, itemId])

  const isYearly = activeTab === 'tahunan'
  const data = summaryData?.data || summaryData

  const monthsData = useMemo(() => {
    if (!isYearly) return []
    return buildYearlyMonths(data)
  }, [isYearly, data])

  const sortedMonths = useMemo(() => {
    if (!isYearly) return []
    return [...monthsData].sort((a, b) => {
      const valA = a.month || 0
      const valB = b.month || 0
      return sortdir === 'ASC' ? valA - valB : valB - valA
    })
  }, [isYearly, monthsData, sortdir])

  const groupedProducts = useMemo(() => groupByItemId(monthlyRows), [monthlyRows])
  const sortedProducts = useMemo(() => [...groupedProducts].sort((a, b) => {
    const comparison = String(a?.name || '').localeCompare(String(b?.name || ''))
    return sortdir === 'ASC' ? comparison : -comparison
  }), [groupedProducts, sortdir])
  const displayData = isYearly ? sortedMonths : sortedProducts.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)
  const totalRows = isYearly ? sortedMonths.length : sortedProducts.length
  const isTableLoading = isSummaryLoading

  const handleSort = (_, direction) => {
    const dir = String(direction || '').toUpperCase() === 'ASC' ? 'ASC' : 'DESC'
    setSortdir(dir)
    setCurrentPage(0)
  }

  const columns = [
    {
      name: <span>{isYearly ? 'Bulan' : 'Barang'}</span>,
      cell: (row) => (
        <div className="d-flex flex-column py-2">
          <span className="fw-semibold">{isYearly ? (row?.label || '-') : (row?.name || '-')}</span>
          {!isYearly && <small className="text-muted">{row.units?.length > 1 ? `${new Set(row.units.map((unitRow) => unitRow.unit)).size} Unit berbeda` : row?.unit || '-'}</small>}
        </div>
      ),
      selector: (row) => (isYearly ? (row?.month || 0) : (row?.name || '-')),
      sortable: true,
      sortField: isYearly ? 'month' : 'name',
      minWidth: isYearly ? '140px' : '180px',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: (
        <span>
          Trx Beli
        </span>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>{row?.purchase?.transactionCount == null ? (isYearly ? '0' : '-') : newFormatThousandSeparator(row.purchase.transactionCount, 0)}</span>
        </div>
      ),
      selector: (row) => row?.purchase?.transactionCount || 0,
      right: true,
      minWidth: '100px',
    },
    ...(!isYearly
      ? [
          {
            name: <span>Vol. Beli</span>,
            cell: (row) => (
              <div className="d-flex justify-content-end w-100">
                <span>{newFormatThousandSeparator(row?.purchase?.effectiveAmount || 0, 2)} {getDisplayUnit(row)}</span>
              </div>
            ),
            selector: (row) => row?.purchase?.effectiveAmount || 0,
            right: true,
            minWidth: '140px',
          },
        ]
      : []),
    {
      name: (
        <span>
          Nilai Beli
        </span>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>
            Rp{newFormatThousandSeparator(row?.purchase?.totalAfterVAT ?? row?.purchase?.totalValue ?? 0, 0)}
          </span>
        </div>
      ),
      selector: (row) => row?.purchase?.totalAfterVAT ?? row?.purchase?.totalValue ?? 0,
      right: true,
      minWidth: '160px',
    },
    {
      name: (
        <span>
          Trx Jual
        </span>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>{row?.sales?.transactionCount == null ? (isYearly ? '0' : '-') : newFormatThousandSeparator(row.sales.transactionCount, 0)}</span>
        </div>
      ),
      selector: (row) => row?.sales?.transactionCount || 0,
      right: true,
      minWidth: '100px',
    },
    ...(!isYearly
      ? [
          {
            name: <span>Vol. Jual</span>,
            cell: (row) => (
              <div className="d-flex justify-content-end w-100">
                <span>{newFormatThousandSeparator(row?.sales?.effectiveAmount || 0, 2)} {getDisplayUnit(row)}</span>
              </div>
            ),
            selector: (row) => row?.sales?.effectiveAmount || 0,
            right: true,
            minWidth: '140px',
          },
        ]
      : []),
    {
      name: (
        <span>
          Nilai Jual
        </span>
      ),
      cell: (row) => (
        <div className="d-flex justify-content-end w-100">
          <span>
            Rp{newFormatThousandSeparator(row?.sales?.totalAfterVAT ?? row?.sales?.totalValue ?? 0, 0)}
          </span>
        </div>
      ),
      selector: (row) => row?.sales?.totalAfterVAT ?? row?.sales?.totalValue ?? 0,
      right: true,
      minWidth: '160px',
    },
    ...(!isYearly
      ? [
          {
            name: <span>Selisih Vol.</span>,
            cell: (row) => {
              const diff = row?.differenceAmount ?? 0
              return (
                <div className="d-flex justify-content-end w-100">
                  <span
                    style={{
                      color: diff >= 0 ? '#027A48' : '#B42318',
                      fontWeight: 600,
                    }}
                  >
                    {diff >= 0 ? '+' : ''}{newFormatThousandSeparator(diff, 2)} {getDisplayUnit(row)}
                  </span>
                </div>
              )
            },
            selector: (row) => row?.differenceAmount ?? 0,
            right: true,
            minWidth: '150px',
          },
        ]
      : []),
    {
      name: <span>Selisih Nilai</span>,
      cell: (row) => {
        const diff = row?.difference?.totalAfterVAT ?? row?.difference?.totalValue ?? row?.differenceValue ?? 0
        return (
          <div className="d-flex justify-content-end w-100">
            <span
              style={{
                color: diff >= 0 ? '#027A48' : '#B42318',
                fontWeight: 600,
              }}
            >
              {diff >= 0 ? '+' : '-'}Rp
              {newFormatThousandSeparator(Math.abs(diff), 0)}
            </span>
          </div>
        )
      },
      selector: (row) => row?.difference?.totalAfterVAT ?? row?.difference?.totalValue ?? row?.differenceValue ?? 0,
      right: true,
      minWidth: '170px',
    },
  ]

  const ExpandedUnits = ({ data: group }) => (
    <div data-testid={`summary-units-${group.id}`}>
      <DataTable
        columns={columns}
        data={group.units}
        pagination={false}
        noTableHead
        expandableRows
        expandableRowDisabled={() => true}
        expandableRowsComponent={() => null}
        expandableIcon={{ collapsed: null, expanded: null }}
        className="border-0 p-0"
        testId={`summary-units-table-${group.id}`}
      />
    </div>
  )

  return (
    <Card style={{ borderRadius: '12px', border: '1px solid #EAECF0' }}>
      <CardBody>
        <h6
          className="fw-semibold mb-3"
          style={{ color: '#344054', fontSize: '0.95rem' }}
        >
          {isYearly ? 'Detail per Bulan' : 'Detail per Barang'}
        </h6>
        <DataTable
          columns={columns}
          data={displayData}
          expandableRows={!isYearly}
          expandableRowDisabled={(row) => row.units?.length <= 1}
          expandOnRowClicked={!isYearly}
          expandableRowsComponent={ExpandedUnits}
          mobileExpandableRows={!isYearly}
          mobileExpandLabel="Unit berbeda"
          noDataComponent={
            <div className="text-muted py-4">
              Tidak ada data transaksi pada periode ini
            </div>
          }
          progressPending={isTableLoading}
          pagination={!isYearly}
          paginationServer={!isYearly}
          paginationTotalRows={totalRows}
          sortIcon={<ChevronUp />}
          onSort={handleSort}
          paginationComponent={!isYearly ? (props) => (
            <PMBPagination
              rowCount={totalRows}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              testId="summary-table-pagination"
              setCurrentPage={(page) => {
                setCurrentPage(page)
              }}
              setRowsPerPage={(size) => {
                setRowsPerPage(size)
                setCurrentPage(0)
              }}
              props={props}
            />
          ) : undefined}
        />
      </CardBody>
    </Card>
  )
}

export default SummaryTable
